// repairStreamingMarkdown.js — A5 pure, zero-dependency, node-testable.
//
// Mid-stream markdown arrives PARTIAL: a `**bold` whose closing `**` has not streamed yet,
// an unclosed ``` code fence, a trailing list bullet with no content. Rendering that raw makes
// react-markdown flash half-syntax (literal `**`, or the rest of the reply swallowed into an
// open code block). This pass closes the dangling tokens so each frame renders as valid markdown.
//
// IMPORT-FREE on purpose: verified by `node --test` over a `*.test.mjs` (no webpack/JSX).
//
// EXPORTED API
//   repairStreamingMarkdown(text) → string

/**
 * Close dangling markdown tokens in a mid-stream fragment so it renders as valid markdown.
 * Handles: unclosed ``` code fences, dangling `**` bold, and a dangling list bullet line.
 * @param {string} text partial markdown emitted so far
 * @returns {string} repaired markdown safe to feed react-markdown each frame
 */
export function repairStreamingMarkdown(text) {
  if (typeof text !== "string" || text.length === 0) return text || ""

  let out = text

  // 1) Unclosed fenced code block: an ODD number of ``` fences means we are mid-block.
  //    Close it so the remainder of the stream isn't swallowed as code.
  const fenceCount = (out.match(/```/g) || []).length
  if (fenceCount % 2 === 1) {
    // Ensure the closing fence sits on its own line.
    out += (out.endsWith("\n") ? "" : "\n") + "```"
  }

  // After the fence is balanced, repair inline emphasis OUTSIDE code blocks only.
  // Re-split on fences: even-indexed segments are non-code, odd-indexed are inside code.
  const segments = out.split("```")
  for (let i = 0; i < segments.length; i += 2) {
    let seg = segments[i]

    // 2) Dangling bold `**`: an odd count of `**` runs means one is unclosed.
    //    (Count `**` occurrences; an odd total → append a closing `**`.)
    const boldCount = (seg.match(/\*\*/g) || []).length
    if (boldCount % 2 === 1) {
      seg += "**"
    }

    segments[i] = seg
  }
  out = segments.join("```")

  // 3) Dangling list bullet: a trailing line that is ONLY a bullet marker (`- `, `* `, `1. `)
  //    with no content renders nothing / looks broken mid-stream. Drop the empty trailing
  //    marker line so the list above it stays clean until real content streams in.
  out = out.replace(/(\n|^)([ \t]*(?:[-*+]|\d+\.)[ \t]*)$/, "$1")

  return out
}

export default repairStreamingMarkdown
